const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class PetKeepsakeHomeMotion {
  constructor() {
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this.revealObserver = null;
  }

  init() {
    document.documentElement.classList.add('pk-home-motion-ready');

    if (!this.reducedMotion) {
      this.setupReveal();
      this.setupHomeHero();
      this.setupTopScrollStage();
    }

    this.setupPhotoStory();
    this.setupCollectionGallery();
    this.setupHowItWorksRainbow();
    this.setupScrollStateCards();
    this.setupPromiseWall();
  }

  setupReveal() {
    const revealItems = document.querySelectorAll(
      [
        '.pk-home-reveal',
        '.pk-home-intro__lede',
        '.pk-home-intro__chip',
        '.pk-home-photo-story__card',
        '.pk-collection-carousel__slide',
        '.ai-bestsellers-card-' + this.getBestsellerId(),
        '.pk-how-it-works-fixed__step',
        '.promise-item',
        '.pk-home-promise-wall__card',
        '.pk-home-reviews__card',
        '.pk-faq__item',
      ].filter(Boolean).join(',')
    );

    if (!revealItems.length || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    revealItems.forEach((item, index) => {
      item.style.setProperty('--pk-reveal-index', String(index % 6));
      this.revealObserver.observe(item);
    });
  }

  setupHomeHero() {
    const hero = document.querySelector('.pk-home-hero');
    if (!hero) return;

    requestAnimationFrame(() => hero.classList.add('is-visible'));
  }

  setupTopScrollStage() {
    const hero = document.querySelector('.pk-home-hero');
    const intro = document.querySelector('.pk-home-intro');
    const photo = document.querySelector('.pk-photo-keepsake-premium');
    if (!hero || !intro || !photo || document.querySelector('[data-pk-top-scroll-stage]')) return;

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
    if (!isDesktop()) return;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const lerp = (start, end, progress) => start + (end - start) * progress;
    const easeOut = (value) => 1 - Math.pow(1 - value, 3);
    const easeInOut = (value) => (value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2);
    const state = { raf: null };

    const stripClone = (node) => {
      node.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      node.removeAttribute('id');
      node.classList.remove('pk-home-reveal', 'pk-home-photo-story__card', 'is-visible');
      node.querySelectorAll('[data-pk-story-card], [data-pk-story-dot], [data-pk-story-rail]').forEach((element) => {
        element.removeAttribute('data-pk-story-card');
        element.removeAttribute('data-pk-story-dot');
        element.removeAttribute('data-pk-story-rail');
      });
      node.querySelectorAll('.pk-home-reveal, .pk-home-photo-story__card, .is-visible').forEach((element) => {
        element.classList.remove('pk-home-reveal', 'pk-home-photo-story__card', 'is-visible');
      });
    };

    const heroSource = hero.closest('.shopify-section') || hero;
    const introSource = intro.closest('.shopify-section') || intro;
    const photoSource = photo.closest('.shopify-section') || photo;
    const stage = document.createElement('section');
    const sticky = document.createElement('div');
    const heroLayer = document.createElement('div');
    const introLayer = document.createElement('div');
    const photoLayer = document.createElement('div');

    stage.className = 'pk-top-scroll-stage';
    stage.dataset.pkTopScrollStage = 'true';
    sticky.className = 'pk-top-scroll-stage__sticky';
    heroLayer.className = 'pk-top-scroll-stage__layer pk-top-scroll-stage__hero';
    introLayer.className = 'pk-top-scroll-stage__layer pk-top-scroll-stage__intro';
    photoLayer.className = 'pk-top-scroll-stage__layer pk-top-scroll-stage__photo';

    const heroClone = hero.cloneNode(true);
    const introClone = intro.querySelector('.pk-home-intro__inner')?.cloneNode(true);
    const photoCopyClone = photo.querySelector('.pk-photo-keepsake-premium__copy')?.cloneNode(true);
    const animationImages = Array.from(photo.querySelectorAll('.pk-photo-keepsake-premium__image-sources img'));
    const preferredImages = Array.from(photo.querySelectorAll('.pk-photo-keepsake-premium__card img'));
    const supportingImages = Array.from(document.querySelectorAll([
      '[data-pk-home-collection-gallery] img',
      '.pk-home-collection img',
      '[class*="bestseller"] img',
      '.product-card img',
      '.card__media img',
    ].join(',')));
    const imageCandidates = [...animationImages, ...preferredImages, ...supportingImages].filter((image, index, images) => {
      const src = image.currentSrc || image.src || image.getAttribute('src');
      return src && images.findIndex((candidate) => (candidate.currentSrc || candidate.src || candidate.getAttribute('src')) === src) === index;
    });

    stripClone(heroClone);
    if (introClone) stripClone(introClone);
    if (photoCopyClone) stripClone(photoCopyClone);

    heroClone.classList.add('pk-top-scroll-stage__hero-clone', 'is-visible');
    heroLayer.appendChild(heroClone);

    if (introClone) {
      introClone.classList.add('pk-top-scroll-stage__intro-inner');
      introLayer.appendChild(introClone);
    }

    const photoCopy = document.createElement('div');
    const photoGrid = document.createElement('div');
    photoCopy.className = 'pk-top-scroll-stage__photo-copy';
    photoGrid.className = 'pk-top-scroll-stage__photo-grid';
    if (photoCopyClone) photoCopy.appendChild(photoCopyClone);

    Array.from({ length: 9 }).forEach((_, index) => {
      const image = imageCandidates[index % Math.max(1, imageCandidates.length)];
      const tile = document.createElement('figure');
      tile.className = 'pk-top-scroll-stage__photo-tile';
      tile.dataset.pkStagePhotoTile = '';
      tile.style.setProperty('--pk-stage-photo-index', String(index));
      tile.style.zIndex = String(20 + index);

      if (image) {
        const clonedImage = image.cloneNode(true);
        clonedImage.removeAttribute('id');
        clonedImage.className = 'pk-top-scroll-stage__photo-image';
        clonedImage.loading = index < 3 ? 'eager' : 'lazy';
        clonedImage.decoding = 'async';
        tile.appendChild(clonedImage);
      } else {
        const placeholder = document.createElement('div');
        placeholder.innerHTML = '<svg viewBox="0 0 120 120" class="placeholder-svg" role="img" aria-label="Product image placeholder"><rect width="120" height="120" fill="#efe4d6"/><circle cx="60" cy="50" r="20" fill="#d8e7f1"/><path d="M22 96c18-24 31-32 40-25 8 6 13 14 21 7 7-6 12-16 22 18H22Z" fill="#f4d8d8"/></svg>';
        tile.appendChild(placeholder.firstChild);
      }

      photoGrid.appendChild(tile);
    });

    photoLayer.appendChild(photoCopy);
    photoLayer.appendChild(photoGrid);
    sticky.append(heroLayer, introLayer, photoLayer);
    stage.appendChild(sticky);
    heroSource.parentNode.insertBefore(stage, heroSource);

    const stageSources = [heroSource, introSource, photoSource];
    const syncSourceAccessibility = () => {
      stageSources.forEach((source) => {
        if (isDesktop()) {
          source.setAttribute('aria-hidden', 'true');
        } else {
          source.removeAttribute('aria-hidden');
        }
      });
    };

    stageSources.forEach((source) => {
      source.classList.add('pk-top-scroll-stage__source-hidden');
    });
    syncSourceAccessibility();

    const photoTiles = Array.from(stage.querySelectorAll('[data-pk-stage-photo-tile]'));
    const photoBody = photoCopy.querySelector('.pk-photo-keepsake-premium__text');
    const photoButtons = photoCopy.querySelector('.pk-photo-keepsake-premium__buttons');
    const photoHeading = photoCopy.querySelector('.pk-photo-keepsake-premium__heading');
    const introChips = Array.from(stage.querySelectorAll('.pk-home-intro__chip'));

    const update = () => {
      state.raf = null;

      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = stage.getBoundingClientRect();
      const scrollable = Math.max(1, stage.offsetHeight - viewport);
      const progress = clamp(-rect.top / scrollable, 0, 1);
      const heroOut = easeInOut(clamp((progress - 0.02) / 0.3, 0, 1));
      const introText = easeOut(clamp((progress - 0.2) / 0.18, 0, 1));
      const introOut = easeInOut(clamp((progress - 0.42) / 0.22, 0, 1));
      const photoIn = easeInOut(clamp((progress - 0.54) / 0.08, 0, 1));
      const photoFlow = easeInOut(clamp((progress - 0.56) / 0.34, 0, 1));
      const photoExpand = easeOut(clamp((progress - 0.88) / 0.075, 0, 1));
      const photoCopyReveal = easeOut(clamp((progress - 0.91) / 0.055, 0, 1));
      const stageExit = 0;

      stage.style.setProperty('--pk-stage-progress', progress.toFixed(4));
      stage.style.setProperty('--pk-stage-hero-out', heroOut.toFixed(4));
      stage.style.setProperty('--pk-stage-intro-text', introText.toFixed(4));
      stage.style.setProperty('--pk-stage-intro-out', introOut.toFixed(4));
      stage.style.setProperty('--pk-stage-photo-in', photoIn.toFixed(4));
      stage.style.setProperty('--pk-stage-photo-flow', photoFlow.toFixed(4));
      stage.style.setProperty('--pk-stage-photo-six', '0');
      stage.style.setProperty('--pk-stage-photo-expand', photoExpand.toFixed(4));
      stage.style.setProperty('--pk-stage-photo-copy', photoCopyReveal.toFixed(4));
      stage.style.setProperty('--pk-stage-exit', stageExit.toFixed(4));

      introChips.forEach((chip, index) => {
        chip.classList.toggle('is-visible', introText >= 0.45 + index * 0.08);
      });

      const width = window.innerWidth || document.documentElement.clientWidth;
      const height = window.innerHeight || document.documentElement.clientHeight;
      const laneCenters = [-0.28, 0, 0.28];
      const travel = lerp(-0.78, 1.9, photoFlow);
      const verticalStep = 0.76;
      const outerStartY = -0.304;
      const centerStartY = 0.304;
      const expandedSlots = {
        3: { x: -0.36, y: 0.44, scale: 1.32 },
        4: { x: 0, y: -0.64, scale: 1.5 },
        5: { x: 0.36, y: 0.44, scale: 1.32 },
        6: { x: -0.36, y: -0.44, scale: 1.32 },
        7: { x: 0, y: 0.66, scale: 1.5 },
        8: { x: 0.36, y: -0.44, scale: 1.32 },
      };

      photoTiles.forEach((tile, index) => {
        const row = Math.floor(index / 3);
        const lane = index % 3;
        const rowOffset = travel - row;
        const laneX = laneCenters[lane] || 0;
        const isCenterLane = lane === 1;
        const laneY = isCenterLane
          ? centerStartY - rowOffset * verticalStep
          : outerStartY + rowOffset * verticalStep;
        const track = {
          x: laneX,
          y: laneY,
          scale: 1,
          rotate: 0,
        };
        const keepTile = Object.prototype.hasOwnProperty.call(expandedSlots, index);
        const expandedSlot = expandedSlots[index] || track;
        const expandAmount = keepTile ? photoExpand : 0;
        const expanded = {
          x: lerp(track.x, expandedSlot.x, expandAmount),
          y: lerp(track.y, expandedSlot.y, expandAmount),
          scale: lerp(track.scale, expandedSlot.scale, expandAmount),
          rotate: track.rotate,
        };
        const tileOpacity = photoIn > 0.02 ? 1 : 0;

        tile.style.setProperty('--pk-stage-photo-x', `${Math.round(expanded.x * width)}px`);
        tile.style.setProperty('--pk-stage-photo-y', `${Math.round(expanded.y * height)}px`);
        tile.style.setProperty('--pk-stage-photo-scale', expanded.scale.toFixed(4));
        tile.style.setProperty('--pk-stage-photo-rotate', `${expanded.rotate.toFixed(3)}deg`);
        tile.style.setProperty('--pk-stage-photo-opacity', tileOpacity.toFixed(4));
      });

      if (photoHeading) photoHeading.style.setProperty('--pk-stage-photo-heading-y', `${Math.round(photoExpand * -18)}px`);
      if (photoBody) photoBody.style.setProperty('--pk-stage-photo-copy-opacity', photoCopyReveal.toFixed(4));
      if (photoButtons) photoButtons.style.setProperty('--pk-stage-photo-copy-opacity', photoCopyReveal.toFixed(4));
    };

    const requestUpdate = () => {
      if (state.raf) return;
      state.raf = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', () => {
      syncSourceAccessibility();
      requestUpdate();
    }, { passive: true });
    update();
  }

  prepareIntroTypography(intro) {
    if (intro.dataset.pkTypographyReady === 'true') return;
    intro.dataset.pkTypographyReady = 'true';

    const targets = Array.from(intro.querySelectorAll('.pk-home-intro__lede, .pk-home-intro__body p'));
    let wordIndex = 0;

    targets.forEach((target) => {
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
      });
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((node) => {
        const fragment = document.createDocumentFragment();
        const parts = node.nodeValue.split(/(\s+)/);
        parts.forEach((part) => {
          if (!part.trim()) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }

          const span = document.createElement('span');
          span.className = 'pk-home-intro__word';
          span.dataset.pkIntroWord = '';
          span.style.setProperty('--pk-word-index', String(wordIndex % 42));
          span.textContent = part;
          wordIndex += 1;
          fragment.appendChild(span);
        });
        node.parentNode.replaceChild(fragment, node);
      });
    });
  }

  setupPhotoStory() {
    const stories = document.querySelectorAll('[data-pk-photo-story]');
    if (!stories.length) return;

    stories.forEach((story) => {
      const cards = Array.from(story.querySelectorAll('[data-pk-story-card]'));
      const dots = Array.from(story.querySelectorAll('[data-pk-story-dot]'));
      const rail = story.querySelector('[data-pk-story-rail]');

      if (!cards.length || !rail) return;

      const setActive = (index) => {
        cards.forEach((card, cardIndex) => {
          card.classList.toggle('is-active', cardIndex === index);
        });

        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
        });
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          setActive(index);
          cards[index]?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
        });
      });

      if ('IntersectionObserver' in window) {
        const cardObserver = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

            if (!visible) return;

            const index = cards.indexOf(visible.target);
            if (index >= 0) setActive(index);
          },
          { root: rail, threshold: [0.45, 0.65, 0.85] }
        );

        cards.forEach((card) => cardObserver.observe(card));
      }

      setActive(0);
    });
  }

  setupCollectionGallery() {
    const galleries = document.querySelectorAll('[data-pk-home-collection-gallery]');
    if (!galleries.length) return;

    galleries.forEach((gallery) => {
      const track = gallery.querySelector('[data-pk-gallery-track]');
      const cards = Array.from(gallery.querySelectorAll('[data-pk-gallery-card]'));
      const prev = gallery.querySelector('[data-pk-gallery-prev]');
      const next = gallery.querySelector('[data-pk-gallery-next]');
      const counter = gallery.querySelector('[data-pk-gallery-current]');
      const titleDisplay = gallery.querySelector('[data-pk-gallery-title-display]');
      const state = { raf: null, activeIndex: 0 };

      if (!track || !cards.length || gallery.dataset.pkGalleryReady === 'true') return;
      gallery.dataset.pkGalleryReady = 'true';

      const setActiveByIndex = (activeIndex) => {
        state.activeIndex = activeIndex;
        cards.forEach((card, index) => {
          card.classList.toggle('is-active', index === activeIndex);
          if (index === activeIndex) {
            counter.textContent = String(index + 1).padStart(2, '0');
            if (titleDisplay) {
              titleDisplay.textContent = card.dataset.pkGalleryTitle || '';
              titleDisplay.href = card.dataset.pkGalleryUrl || titleDisplay.href;
            }
          }
        });
      };

      const setActiveFromMobileTrack = () => {
        const trackRect = track.getBoundingClientRect();
        const center = trackRect.left + trackRect.width / 2;
        const active = cards
          .map((card, index) => {
            const rect = card.getBoundingClientRect();
            return { index, distance: Math.abs(rect.left + rect.width / 2 - center) };
          })
          .sort((a, b) => a.distance - b.distance)[0];
        if (active) setActiveByIndex(active.index);
      };

      const updatePinnedGallery = () => {
        state.raf = null;

        if (window.matchMedia('(max-width: 749px)').matches) {
          cards.forEach((card) => {
            card.style.removeProperty('--pk-card-x');
            card.style.removeProperty('--pk-card-rotate');
            card.style.removeProperty('--pk-card-scale');
            card.style.removeProperty('--pk-card-opacity');
          });
          setActiveFromMobileTrack();
          return;
        }

        const rect = gallery.getBoundingClientRect();
        const scrollable = Math.max(1, gallery.offsetHeight - window.innerHeight);
        const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
        const maxIndex = Math.max(0, cards.length - 1);
        const virtualIndex = progress * maxIndex;
        const activeIndex = Math.min(maxIndex, Math.max(0, Math.round(virtualIndex)));
        const spread = Math.min(window.innerWidth * 0.34, 520);
        const openingOffset = -Math.min(window.innerWidth * 0.18, 330) * Math.max(0, 1 - progress / 0.18);

        cards.forEach((card, index) => {
          const distance = index - virtualIndex;
          const abs = Math.abs(distance);
          const visibleDistance = Math.max(-2.35, Math.min(2.35, distance));
          const scale = Math.max(0.7, 1 - abs * 0.1);
          const opacity = abs > 2.65 ? 0 : Math.max(0.18, 1 - abs * 0.24);
          const rotate = Math.max(-18, Math.min(18, distance * -12));

          card.style.setProperty('--pk-card-x', `${openingOffset + visibleDistance * spread}px`);
          card.style.setProperty('--pk-card-rotate', `${rotate}deg`);
          card.style.setProperty('--pk-card-scale', scale.toFixed(4));
          card.style.setProperty('--pk-card-opacity', opacity.toFixed(4));
          card.style.zIndex = String(40 - Math.round(abs * 8));
        });

        setActiveByIndex(activeIndex);
      };

      const requestPinnedUpdate = () => {
        if (state.raf) return;
        state.raf = window.requestAnimationFrame(updatePinnedGallery);
      };

      const scrollByCard = (direction) => {
        if (!window.matchMedia('(max-width: 749px)').matches) {
          const step = Math.max(window.innerHeight * 0.78, (gallery.offsetHeight - window.innerHeight) / Math.max(1, cards.length - 1));
          window.scrollBy({ top: direction * step, behavior: this.reducedMotion ? 'auto' : 'smooth' });
          return;
        }

        const cardWidth = cards[0]?.getBoundingClientRect().width || track.clientWidth * 0.8;
        track.scrollBy({ left: direction * cardWidth, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      };

      prev?.addEventListener('click', () => scrollByCard(-1));
      next?.addEventListener('click', () => scrollByCard(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(setActiveFromMobileTrack), { passive: true });
      window.addEventListener('scroll', requestPinnedUpdate, { passive: true });
      window.addEventListener('resize', requestPinnedUpdate, { passive: true });
      updatePinnedGallery();
    });
  }

  setupHowItWorksRainbow() {
    const sections = document.querySelectorAll('[data-pk-how-rainbow]');
    if (!sections.length) return;

    sections.forEach((section) => this.initHowItWorksRainbowSection(section));
  }

  initHowItWorksRainbowSection(section) {
    const steps = Array.from(section.querySelectorAll('[data-pk-how-step]'));
    if (!steps.length || section.dataset.pkRainbowReady === 'true') return;
    section.dataset.pkRainbowReady = 'true';

    const isMobile = () => window.matchMedia('(max-width: 989px)').matches;
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const state = { maxRevealedStep: -1, raf: null };

    const revealSteps = (progress) => {
      const thresholds = isMobile() ? [0.04, 0.24, 0.5] : [0.08, 0.38, 0.7];
      thresholds.forEach((threshold, index) => {
        if (progress >= threshold) state.maxRevealedStep = Math.max(state.maxRevealedStep, index);
      });

      const current = thresholds.reduce((active, threshold, index) => (progress >= threshold ? index : active), -1);
      steps.forEach((step, index) => {
        const revealed = index <= state.maxRevealedStep;
        step.classList.toggle('is-revealed', revealed);
        step.classList.toggle('is-current', revealed && index === current);
      });
    };

    const updateScroll = () => {
      state.raf = null;
      const rect = section.getBoundingClientRect();
      const scrollable = Math.max(1, section.offsetHeight - window.innerHeight);
      const progress = clamp(-rect.top / scrollable, 0, 1);
      section.style.setProperty('--pk-how-progress', progress.toFixed(4));
      section.style.setProperty('--pk-how-paint-progress', clamp(progress / 0.5, 0, 1).toFixed(4));
      section.style.setProperty('--pk-how-flight-progress', clamp((progress - 0.5) / 0.5, 0, 1).toFixed(4));
      revealSteps(progress);
    };

    updateScroll();
    const requestUpdate = () => {
      if (state.raf) return;
      state.raf = window.requestAnimationFrame(updateScroll);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });

    if (this.reducedMotion) {
      section.style.setProperty('--pk-how-progress', '1');
      section.style.setProperty('--pk-how-paint-progress', '1');
      section.style.setProperty('--pk-how-flight-progress', '1');
      steps.forEach((step) => {
        step.classList.add('is-revealed');
      });
    }
  }

  setupScrollStateCards() {
    const cards = document.querySelectorAll('[data-pk-stack-card]');
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      cards.forEach((card) => card.classList.add('is-active'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-active', entry.isIntersecting);
        });
      },
      { rootMargin: '-18% 0px -42% 0px', threshold: 0.2 }
    );

    cards.forEach((card) => observer.observe(card));

    document.querySelectorAll('[data-pk-how-story]').forEach((story) => {
      const pathObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-path-active', entry.isIntersecting);
          });
        },
        { rootMargin: '-20% 0px -20% 0px', threshold: 0.22 }
      );

      pathObserver.observe(story);
    });
  }

  setupPromiseWall() {
    const walls = document.querySelectorAll('[data-pk-promise-wall]');
    if (!walls.length) return;

    walls.forEach((wall) => {
      if (wall.dataset.pkPromiseReady === 'true') return;
      wall.dataset.pkPromiseReady = 'true';

      const cards = Array.from(wall.querySelectorAll('[data-pk-promise-card]'));
      cards.forEach((card) => {
        const button = card.querySelector('button');
        if (!button) return;

        button.addEventListener('click', () => {
          const shouldOpen = !card.classList.contains('is-open');
          cards.forEach((other) => {
            other.classList.remove('is-open');
            other.querySelector('button')?.setAttribute('aria-expanded', 'false');
          });

          card.classList.toggle('is-open', shouldOpen);
          button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        });
      });
    });
  }

  getBestsellerId() {
    const section = document.querySelector('[class*="ai-bestsellers-carousel-"]');
    if (!section) return '';

    const match = Array.from(section.classList).find((className) => className.startsWith('ai-bestsellers-carousel-'));
    return match ? match.replace('ai-bestsellers-carousel-', '') : '';
  }
}

function initPetKeepsakeHomeMotion() {
  new PetKeepsakeHomeMotion().init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPetKeepsakeHomeMotion, { once: true });
} else {
  initPetKeepsakeHomeMotion();
}

document.addEventListener('shopify:section:load', initPetKeepsakeHomeMotion);
